import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from './common/Tooltip';
import DonutChart from './DonutChart';
import { parseFloatFromString, formatCurrency, formatPercentage, getChangeColorClass, formatChangedCurrencyAmount, formatChangedPercentage } from '../utils/formatters';
import { positionsListNavState } from '../utils/positionsListNavState';
import { usePositionContext } from '../contexts/PositionContext';

const formatSegmentTitle = (label, positions, pnl, roi) =>
  `${label} (${positions}) - PnL ${formatChangedCurrencyAmount(pnl)} (${formatChangedPercentage(roi)})`;

const COLORS = [
  '#A855F7', // purple-500
  '#6366F1', // indigo-500
  '#EC4899', // pink-500
  '#14B8A6', // teal-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
  '#F59E0B', // amber-500
  '#F43F5E', // rose-500
  '#D946EF', // fuchsia-500
];

const VIEW_OPTIONS = [
  { id: 'current', label: 'Current' },
  { id: 'invested', label: 'Invested' },
];

const MAX_VISIBLE = 5;

const GroupBreakdown = ({ groupCategories = [] }) => {
  const [view, setView] = useState('current');
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { positions = [] } = usePositionContext();

  // Load saved view preference
  useEffect(() => {
    const saved = localStorage.getItem('dashboard_group_view');
    if (saved && VIEW_OPTIONS.some((v) => v.id === saved)) {
      setView(saved);
    }
  }, []);

  const handleViewChange = (newView) => {
    setView(newView);
    localStorage.setItem('dashboard_group_view', newView);
  };

  // Count positions per group
  const positionsCountByGroup = useMemo(() => {
    const counts = {};
    positions.forEach((p) => {
      const amt = parseFloatFromString(p?.holdingsAmount);
      if (!amt || amt <= 0) return;
      const groupName = p?.token?.group?.name || 'Ungrouped';
      counts[groupName] = (counts[groupName] || 0) + 1;
    });
    return counts;
  }, [positions]);

  // Process and sort groups
  const rows = useMemo(() => {
    const totals = { holdings: 0, invested: 0 };

    const processed = (groupCategories || []).map((c, idx) => {
      const value = parseFloatFromString(c.value);
      const totalCost = parseFloatFromString(c.costs); // costs is now totalCost
      const proceeds = parseFloatFromString(c.proceeds);
      const invested = totalCost || 0;
      const pnl = (value || 0) + (proceeds || 0) - (totalCost || 0);
      const posCount = positionsCountByGroup[c.name] || 0;

      totals.holdings += value || 0;
      totals.invested += invested;

      return {
        name: c.name,
        value: value || 0,
        invested,
        pnl,
        positions: posCount,
        color: COLORS[idx % COLORS.length],
      };
    });

    // Sort by value descending
    processed.sort((a, b) => b.value - a.value);

    // Add percentages and ROI
    return processed.map((r) => ({
      ...r,
      holdingPct: totals.holdings > 0 ? (r.value / totals.holdings) * 100 : 0,
      investedPct: totals.invested > 0 ? (r.invested / totals.invested) * 100 : 0,
      roi: r.invested > 0 ? ((r.value - r.invested) / r.invested) * 100 : 0,
    }));
  }, [groupCategories, positionsCountByGroup]);

  // Calculate totals for donut center
  const totals = useMemo(() => ({
    holdings: rows.reduce((sum, r) => sum + r.value, 0),
    invested: rows.reduce((sum, r) => sum + r.invested, 0),
  }), [rows]);

  // Chart data based on view (with tooltip for donut segments)
  const chartData = useMemo(() => {
    return rows.map((r) => ({
      name: r.name,
      value: view === 'invested' ? r.invested : r.value,
      color: r.color,
      tooltip: formatSegmentTitle(r.name, r.positions, r.pnl, r.roi),
    }));
  }, [rows, view]);

  // Visible rows
  const visibleRows = expanded ? rows : rows.slice(0, MAX_VISIBLE);
  const hiddenCount = rows.length - MAX_VISIBLE;

  // Don't show if no groups or only "Ungrouped"
  const hasRealGroups = rows.some((r) => r.name !== 'Ungrouped' && r.name !== 'ungrouped');
  if (rows.length === 0 || !hasRealGroups) {
    return null;
  }

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      {/* Header with View Toggle in middle */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Groups</h3>
        <div className="flex gap-1">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => handleViewChange(opt.id)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${
                view === opt.id
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => navigate('/groups')}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          Edit Groups
        </button>
      </div>

      {/* Donut Chart + Combined Legend/Table */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Donut Chart */}
        <div className="relative flex-shrink-0 flex items-center justify-center mx-auto sm:mx-0" style={{ width: 140, height: 140 }}>
          <DonutChart
            data={chartData.slice(0, MAX_VISIBLE)}
            animationKey={view}
            size={140}
            thickness={26}
            totalLabel=""
            showLabels={false}
            showLegend={false}
            onSegmentClick={(segment) => navigate('/positions', { state: positionsListNavState({ groupName: segment.name }) })}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {view === 'invested' ? 'Invested' : 'Holdings'}
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(view === 'invested' ? totals.invested : totals.holdings)}
            </span>
          </div>
        </div>

        {/* Combined Legend/Table */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 dark:text-gray-500 text-[10px]">
                <th className="text-left font-normal pb-1"></th>
                {view === 'current' && (
                  <>
                    <th className="text-right font-normal pb-1">%</th>
                    <th className="text-right font-normal pb-1">Holdings</th>
                    <th className="text-right font-normal pb-1">ROI</th>
                  </>
                )}
                {view === 'invested' && (
                  <>
                    <th className="text-right font-normal pb-1">%</th>
                    <th className="text-right font-normal pb-1">Invested</th>
                    <th className="text-right font-normal pb-1">PnL</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, idx) => (
                <tr
                  key={r.name}
                  className={idx > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}
                >
                  <td className="py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: r.color }} />
                      <Tooltip title={formatSegmentTitle(r.name, r.positions, r.pnl, r.roi)} position="top" arrow={true} theme="dark" size="small">
                        <button
                          onClick={() => navigate('/positions', { state: positionsListNavState({ groupName: r.name }) })}
                          className="text-gray-800 dark:text-gray-200 hover:underline text-left truncate"
                        >
                          {r.name} ({r.positions})
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                  {view === 'current' && (
                    <>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                        {formatPercentage(r.holdingPct)}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatCurrency(r.value)}
                      </td>
                      <td className={`py-1.5 text-right font-medium tabular-nums ${getChangeColorClass(r.roi)}`}>
                        {r.roi >= 0 ? '+' : ''}{formatPercentage(r.roi)}
                      </td>
                    </>
                  )}
                  {view === 'invested' && (
                    <>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                        {formatPercentage(r.investedPct)}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatCurrency(r.invested)}
                      </td>
                      <td className={`py-1.5 text-right font-medium tabular-nums ${getChangeColorClass(r.pnl)}`}>
                        {formatCurrency(r.pnl)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Show More */}
          {hiddenCount > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more...`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupBreakdown;
